/**
 * MarketingModule — Marketing tab for divisions that track spend + campaigns.
 *
 * Widgets (in order of appearance):
 *   1. Totals strip: Spend · Revenue · ROI · Top channel
 *   2. Spend by Channel (table + bar)
 *   3. Revenue by Channel (from daily_sales.sub_amounts)
 *   4. ROI by Channel (attribution mapping)
 *   5. Campaigns (active + past) + Upcoming calendar
 *   6. Phase-2 Connectors status (Meta Ads, Google Ads, GA4, Shopify Admin)
 *
 * Entry modals: Add Spend · Add Campaign
 *
 * Backend: /api/marketing/* (see backend/routes/marketing_routes.py)
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Plus, Megaphone, TrendingUp, Link as LinkIcon, X, Trash2, AlertCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';

const INR = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const LAKH = (n) => `₹${(n / 1e5).toFixed(2)}L`;

export default function MarketingModule({ divisionId, divisionName }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [shopify, setShopify] = useState(null);
  const [error, setError] = useState('');
  const [showAddSpend, setShowAddSpend] = useState(false);
  const [showAddCampaign, setShowAddCampaign] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/marketing/dashboard/${divisionId}`),
        api.get(`/shopify/orders/summary/${divisionId}`).catch(() => ({ data: null })),
      ]);
      setData(r1.data);
      setShopify(r2?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load marketing dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [divisionId]);

  if (loading) return <div className="p-8 text-center text-sm text-[#6B7280]">Loading marketing data…</div>;
  if (error) return <div className="p-8 text-center text-sm text-[#D4726A]">{error}</div>;
  if (!data) return null;

  const { totals, spend_by_channel, revenue_by_channel, roi_by_channel,
          campaigns, upcoming_campaigns, connectors, channels, shopify_stats,
          cac, webhook_status } = data;

  return (
    <div className="space-y-4" data-testid="marketing-module">
      {/* Header + action buttons */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone size={18} style={{ color: '#2A7B88' }} />
            <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Marketing · {divisionName}</h2>
          </div>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            FY {data.fy} · spend → orders → ROI per channel. Phase 1: manual spend entry. Phase 2: Meta/Google/GA4/Shopify auto-sync.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAddSpend(true)} className="px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5"
            style={{ backgroundColor: '#2A7B88' }} data-testid="add-spend-btn">
            <Plus size={14} /> Add Spend
          </button>
          <button onClick={() => setShowAddCampaign(true)} className="px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1.5 border"
            style={{ borderColor: '#2A7B88', color: '#2A7B88' }} data-testid="add-campaign-btn">
            <Plus size={14} /> Add Campaign
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Spend FYTD" value={LAKH(totals.spend)} accent="#BA7517" testid="stat-spend" />
        <Stat label="Revenue FYTD" value={LAKH(totals.revenue)} accent="#2D8B5E" testid="stat-revenue" />
        <Stat label="Blended ROI" value={`${totals.roi}x`} accent="#2A7B88"
              sub={totals.roi > 5 ? 'healthy' : totals.roi > 2 ? 'ok' : 'needs work'} testid="stat-roi" />
        <Stat label="Top Channel (spend)"
              value={spend_by_channel[0]?.channel || '—'}
              sub={spend_by_channel[0] ? LAKH(spend_by_channel[0].amount) : null}
              accent="#7B5EA7" testid="stat-top-channel" />
      </div>

      {/* CAC + Webhook-live indicator */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CACCard cac={cac} />
        <WebhookStatusCard ws={webhook_status} />
      </div>

      {/* Shopify live feed — from Flow webhook */}
      <ShopifyWidget shopify={shopify} />

      {/* Spend by Channel */}
      <Card title="Spend by Channel" subtitle={`${spend_by_channel.length} channel${spend_by_channel.length === 1 ? '' : 's'} this FY`}>
        {spend_by_channel.length === 0 ? (
          <EmptyState msg="No spend logged yet this FY." cta="Add your first spend entry" onClick={() => setShowAddSpend(true)} />
        ) : (
          <>
            <div style={{ height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={spend_by_channel} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
                  <XAxis dataKey="channel" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v/1000}k`} />
                  <Tooltip formatter={(v) => INR(v)} />
                  <Bar dataKey="amount" radius={[4,4,0,0]}>
                    {spend_by_channel.map((_, i) => <Cell key={i} fill={['#BA7517','#2A7B88','#7B5EA7','#3B5BA9','#2D8B5E','#D4726A'][i % 6]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <SpendTable items={spend_by_channel} />
          </>
        )}
      </Card>

      {/* Revenue by Channel (from daily_sales.sub_amounts) */}
      <Card title="Revenue by Channel (from Daily Sales)"
            subtitle="Auto-pulled from TDB daily sales entries — Online, Store, Zomato, Swiggy, Delivery, B2B">
        {revenue_by_channel.length === 0 ? (
          <EmptyState msg="No revenue data yet this FY." />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {revenue_by_channel.map(r => (
              <div key={r.channel} className="p-3 rounded-md border flex items-center justify-between" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF7' }}>
                <span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{r.channel}</span>
                <span className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{LAKH(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ROI by Channel */}
      <Card title="ROI by Channel" subtitle="Spend vs attributed revenue per channel. Attribution is approximate until Shopify Admin + Meta/Google APIs are connected.">
        {roi_by_channel.length === 0 ? (
          <EmptyState msg="Add spend to see ROI calculations." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                  <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Channel</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Spend</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Revenue (attr.)</th>
                  <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>ROI</th>
                </tr>
              </thead>
              <tbody>
                {roi_by_channel.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }} data-testid={`roi-row-${r.channel}`}>
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{r.channel}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{INR(r.spend)}</td>
                    <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{INR(r.revenue)}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: r.roi_multiplier > 5 ? '#2D8B5E' : r.roi_multiplier > 2 ? '#BA7517' : '#D4726A' }}>
                      {r.roi_multiplier}x
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Campaigns + Upcoming */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card title={`Campaigns (${campaigns.length})`} subtitle="Named campaigns with linked spend + revenue">
          {campaigns.length === 0 ? (
            <EmptyState msg="No campaigns yet." cta="Add first campaign" onClick={() => setShowAddCampaign(true)} />
          ) : (
            <div className="space-y-2">
              {campaigns.slice(0, 5).map(c => <CampaignRow key={c.id} c={c} onRefresh={load} />)}
              {campaigns.length > 5 && <p className="text-[10px] text-[#9CA3AF] italic">+{campaigns.length - 5} more</p>}
            </div>
          )}
        </Card>
        <Card title={`Upcoming Campaigns (${upcoming_campaigns.length})`} subtitle="Planned + running campaigns">
          {upcoming_campaigns.length === 0 ? (
            <EmptyState msg="No campaigns scheduled." />
          ) : (
            <div className="space-y-1.5">
              {upcoming_campaigns.map(c => (
                <div key={c.id} className="px-3 py-2 rounded-md border flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: '#1B2A4A' }}>{c.name}</p>
                    <p className="text-[10px]" style={{ color: '#6B7280' }}>{c.channel} · {c.start_date} → {c.end_date}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                    style={{ backgroundColor: c.status === 'running' ? '#DCFCE7' : '#FEF3C7',
                             color: c.status === 'running' ? '#14532D' : '#78350F' }}>{c.status}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Phase 2 Connectors */}
      <Card title="Phase 2 — API Connectors (plumbing ready)"
            subtitle="Drop credentials into backend/.env to flip any connector live. No code change needed.">
        <div className="grid md:grid-cols-2 gap-2">
          {connectors.map(c => (
            <div key={c.key} className="p-3 rounded-md border flex items-start justify-between gap-3"
              style={{ borderColor: c.status === 'configured' ? '#2D8B5E' : '#E5E1DC',
                       backgroundColor: c.status === 'configured' ? '#F0FDF4' : '#FAFAF7' }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <LinkIcon size={12} style={{ color: c.status === 'configured' ? '#2D8B5E' : '#9CA3AF' }} />
                  <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{c.label}</span>
                </div>
                <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>
                  {c.status === 'configured' ? 'Credentials detected — ready to sync.'
                   : `Set these: ${c.env_vars_needed.join(', ')}`}
                </p>
                <a href={c.docs_url} target="_blank" rel="noreferrer" className="text-[10px] mt-1 inline-block hover:underline" style={{ color: '#2A7B88' }}>
                  API docs →
                </a>
              </div>
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase flex-shrink-0"
                style={{ backgroundColor: c.status === 'configured' ? '#2D8B5E' : '#F3F4F6',
                         color: c.status === 'configured' ? '#fff' : '#6B7280' }}>
                {c.status === 'configured' ? 'Ready' : 'Not connected'}
              </span>
            </div>
          ))}
        </div>
        {shopify_stats.products_synced > 0 && (
          <div className="mt-3 p-2.5 rounded-md text-[11px]" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
            <strong>Shopify catalog:</strong> {shopify_stats.products_synced} products already synced. Orders + customers arrive when Shopify Admin token is added.
          </div>
        )}
      </Card>

      {/* Modals */}
      {showAddSpend && (
        <AddSpendModal
          divisionId={divisionId}
          channels={channels}
          campaignNames={campaigns.map(c => c.name)}
          onClose={() => setShowAddSpend(false)}
          onSaved={() => { setShowAddSpend(false); load(); }}
        />
      )}
      {showAddCampaign && (
        <AddCampaignModal
          divisionId={divisionId}
          channels={channels}
          onClose={() => setShowAddCampaign(false)}
          onSaved={() => { setShowAddCampaign(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────
function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className="p-3 rounded-md border bg-white" style={{ borderColor: '#E5E1DC' }} data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-lg font-bold mt-1" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{sub}</p>}
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
      <div className="mb-3">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{title}</h3>
        {subtitle && <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ msg, cta, onClick }) {
  return (
    <div className="py-6 text-center">
      <AlertCircle size={20} className="mx-auto" style={{ color: '#9CA3AF' }} />
      <p className="text-xs mt-2" style={{ color: '#6B7280' }}>{msg}</p>
      {cta && <button onClick={onClick} className="mt-2 text-xs font-semibold hover:underline" style={{ color: '#2A7B88' }}>{cta} →</button>}
    </div>
  );
}

function SpendTable({ items }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
            <th className="px-3 py-2 font-semibold" style={{ color: '#6B7280' }}>Channel</th>
            <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Total Spend</th>
            <th className="px-3 py-2 font-semibold text-right" style={{ color: '#6B7280' }}>Months</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
              <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{r.channel}</td>
              <td className="px-3 py-2 text-right font-bold" style={{ color: '#BA7517' }}>{INR(r.amount)}</td>
              <td className="px-3 py-2 text-right" style={{ color: '#6B7280' }}>{r.months_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignRow({ c, onRefresh }) {
  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/marketing/campaigns/${c.id}`);
      onRefresh?.();
    } finally { setDeleting(false); }
  };
  return (
    <div className="px-3 py-2 rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid={`campaign-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold truncate" style={{ color: '#1B2A4A' }}>{c.name}</p>
          <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{c.channel} · {c.start_date} → {c.end_date}</p>
        </div>
        <button onClick={del} disabled={deleting} className="p-1 rounded hover:bg-red-50" data-testid={`delete-campaign-${c.id}`}>
          <Trash2 size={11} style={{ color: '#D4726A' }} />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mt-2">
        <MiniKv label="Budget" value={INR(c.budget)} />
        <MiniKv label="Spend" value={INR(c.actual_spend)} accent={c.actual_spend > c.budget ? '#D4726A' : '#6B7280'} />
        <MiniKv label="Revenue" value={INR(c.actual_revenue)} accent="#2D8B5E" />
        <MiniKv label="ROI" value={c.actual_spend > 0 ? `${c.roi_multiplier}x` : '—'} accent={c.roi_multiplier > 3 ? '#2D8B5E' : '#BA7517'} />
      </div>
    </div>
  );
}

function MiniKv({ label, value, accent = '#1B2A4A' }) {
  return (
    <div>
      <p className="text-[9px]" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-[11px] font-bold" style={{ color: accent }}>{value}</p>
    </div>
  );
}

// ─── Add Spend Modal ────────────────────────────────────────────────────────
function AddSpendModal({ divisionId, channels, campaignNames, onClose, onSaved }) {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState({
    month: thisMonth, channel: channels[0] || 'Instagram Ads',
    amount: '', campaign_name: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Amount must be greater than 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/marketing/spend', {
        division_id: divisionId,
        month: form.month,
        channel: form.channel,
        amount: parseFloat(form.amount),
        campaign_name: form.campaign_name || '',
        notes: form.notes || '',
      });
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="add-spend-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Add Marketing Spend</h3>
          <button onClick={onClose} className="p-1" data-testid="close-spend-modal"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Month"><input type="month" value={form.month} onChange={(e) => setForm({...form, month: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="spend-month" /></Field>
          <Field label="Channel">
            <select value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="spend-channel">
              {channels.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Amount (₹)">
            <input type="number" min="0" step="100" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="e.g. 25000" className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="spend-amount" />
          </Field>
          <Field label="Campaign (optional — link spend to a named campaign for ROI)">
            <input list="campaigns-list" value={form.campaign_name} onChange={(e) => setForm({...form, campaign_name: e.target.value})} placeholder="e.g. April Paw Pack" className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="spend-campaign" />
            <datalist id="campaigns-list">{campaignNames.map(n => <option key={n} value={n} />)}</datalist>
          </Field>
          <Field label="Notes (optional)">
            <input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="spend-notes" />
          </Field>
        </div>
        {error && <p className="mt-2 text-xs" style={{ color: '#D4726A' }}>{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded-md border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-semibold rounded-md text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="save-spend-btn">
            {saving ? 'Saving…' : 'Save Spend'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Campaign Modal ─────────────────────────────────────────────────────
function AddCampaignModal({ divisionId, channels, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: '', channel: channels[0] || 'Instagram Ads',
    start_date: today, end_date: today, budget: '', goal: '', status: 'planned',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!form.name.trim()) { setError('Campaign name required.'); return; }
    if (new Date(form.end_date) < new Date(form.start_date)) { setError('End date must be after start date.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post('/marketing/campaigns', {
        division_id: divisionId,
        name: form.name.trim(),
        channel: form.channel,
        start_date: form.start_date,
        end_date: form.end_date,
        budget: parseFloat(form.budget) || 0,
        goal: form.goal,
        status: form.status,
      });
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="add-campaign-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>Add Campaign</h3>
          <button onClick={onClose} className="p-1" data-testid="close-campaign-modal"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Campaign Name">
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="e.g. April Paw Pack Launch" className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-name" />
          </Field>
          <Field label="Primary Channel">
            <select value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-channel">
              {channels.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start Date">
              <input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-start" />
            </Field>
            <Field label="End Date">
              <input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-end" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Budget (₹)">
              <input type="number" min="0" value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} placeholder="30000" className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-budget" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-status">
                <option value="planned">Planned</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
          </div>
          <Field label="Goal (optional — what does success look like?)">
            <input value={form.goal} onChange={(e) => setForm({...form, goal: e.target.value})} placeholder="e.g. 500 orders from new customers" className="w-full border rounded-md px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="campaign-goal" />
          </Field>
        </div>
        {error && <p className="mt-2 text-xs" style={{ color: '#D4726A' }}>{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded-md border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-semibold rounded-md text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="save-campaign-btn">
            {saving ? 'Saving…' : 'Save Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ─── Shopify live widget (from Flow webhook) ────────────────────────────────
function ShopifyWidget({ shopify }) {
  if (!shopify) return null;
  const live = shopify.webhook_live;
  const latest = shopify.latest_order;

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: live ? '#2D8B5E' : '#E5E1DC' }} data-testid="shopify-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: live ? '#2D8B5E' : '#9CA3AF' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Shopify Live Orders (via Flow webhook)</h3>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase"
                  style={{ backgroundColor: live ? '#DCFCE7' : '#F3F4F6',
                           color: live ? '#14532D' : '#6B7280' }}>
              {live ? 'Connected' : 'Waiting for first order'}
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
            {live
              ? `Orders stream in automatically whenever Shopify Flow fires. ${shopify.total_orders} orders captured so far.`
              : "Set up on Shopify Flow side — once a new order hits your store it appears here within seconds. Historical orders arrive via CSV import."}
          </p>
        </div>
      </div>

      {live && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
          <MiniStat label="Total Orders" value={shopify.total_orders} accent="#2A7B88" />
          <MiniStat label="Last 30 Days" value={shopify.orders_30d} accent="#3B5BA9" />
          <MiniStat label="Revenue Captured" value={shopify.total_revenue >= 100000 ? `₹${(shopify.total_revenue/1e5).toFixed(2)}L` : `₹${Math.round(shopify.total_revenue).toLocaleString('en-IN')}`} accent="#2D8B5E" />
          <MiniStat label="Unique Customers" value={shopify.unique_customers} accent="#7B5EA7" />
          <MiniStat label="Repeat Rate" value={`${shopify.repeat_rate_pct}%`} sub={`${shopify.repeat_customers} returning`} accent="#BA7517" />
        </div>
      )}

      {live && latest && (
        <div className="mt-3 p-2.5 rounded-md text-[11px] flex items-center justify-between gap-2" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
          <span>
            <strong style={{ color: '#1B2A4A' }}>Latest order:</strong> {latest.name || latest.order_id?.slice(-6)}
            {latest.total ? ` · ₹${Math.round(latest.total).toLocaleString('en-IN')}` : ''}
            {latest.city ? ` · ${latest.city}` : ''}
            {latest.email ? ` · ${latest.email}` : ''}
          </span>
          <span className="text-[10px]">{latest.received_at ? new Date(latest.received_at).toLocaleString('en-IN') : ''}</span>
        </div>
      )}

      {!live && (
        <div className="mt-3 p-3 rounded-md text-[11px]" style={{ backgroundColor: '#FFFBEB', color: '#78350F', borderLeft: '3px solid #F59E0B' }}>
          <strong>Setup status:</strong> Shopify Flow "Send order data to ScaleBoard on order updated" is configured. Waiting for the first live order to arrive after production redeploy.
          For historical orders, export the full Shopify orders CSV and send to Dipali — we'll backfill in one shot.
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub, accent = '#1B2A4A' }) {
  return (
    <div className="p-2 rounded-md" style={{ backgroundColor: '#FAFAF7' }}>
      <p className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-sm font-bold mt-0.5" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[9px] mt-0.5" style={{ color: '#9CA3AF' }}>{sub}</p>}
    </div>
  );
}


function CACCard({ cac }) {
  if (!cac) return null;
  const hasSpend = (cac.total_spend || 0) > 0;
  const color = cac.cac == null ? '#6B7280' : cac.cac < 1000 ? '#2D8B5E' : cac.cac < 3000 ? '#BA7517' : '#D4726A';
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF7' }} data-testid="cac-card">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>Customer Acquisition Cost (FYTD)</p>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>spend ÷ first-time-buyer emails</p>
        </div>
        <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>Shopify-powered</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <div>
          <p className="text-[9px]" style={{ color: '#9CA3AF' }}>SPEND</p>
          <p className="text-sm font-bold" style={{ color: '#BA7517' }}>{INR(cac.total_spend || 0)}</p>
        </div>
        <div>
          <p className="text-[9px]" style={{ color: '#9CA3AF' }}>NEW CUSTOMERS</p>
          <p className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{(cac.new_customers || 0).toLocaleString('en-IN')}</p>
        </div>
        <div>
          <p className="text-[9px]" style={{ color: '#9CA3AF' }}>CAC</p>
          <p className="text-xl font-bold" style={{ color }} data-testid="cac-value">
            {cac.cac == null ? (hasSpend ? '—' : 'n/a') : INR(cac.cac)}
          </p>
        </div>
      </div>
      {!hasSpend && (
        <p className="text-[10px] mt-2 italic" style={{ color: '#9CA3AF' }}>
          Log marketing spend to see CAC. {cac.new_customers > 0 && `${cac.new_customers} first-time buyers captured so far this FY.`}
        </p>
      )}
    </div>
  );
}

function WebhookStatusCard({ ws }) {
  if (!ws) return null;
  const isLive = ws.is_live;
  const hasCSV = (ws.csv_orders_count || 0) > 0;
  // Green when we have data (either live OR CSV historical). Red only if truly empty.
  const status = isLive ? 'live' : (hasCSV ? 'historical' : 'empty');
  const dotColor = status === 'live' ? '#2D8B5E' : status === 'historical' ? '#2A7B88' : '#D4726A';
  const badgeBg = status === 'live' ? '#DCFCE7' : status === 'historical' ? '#E6F1FB' : '#FEF2F2';
  const badgeFg = status === 'live' ? '#166534' : status === 'historical' ? '#185FA5' : '#991B1B';
  const badgeText = status === 'live' ? 'LIVE' : status === 'historical' ? 'HISTORICAL' : 'NO DATA';

  return (
    <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF7' }} data-testid="webhook-status-card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: dotColor }} />
          <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>Shopify Orders</p>
        </div>
        <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: badgeBg, color: badgeFg }} data-testid="webhook-live-badge">
          {badgeText}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <p className="text-[9px]" style={{ color: '#9CA3AF' }}>LIVE ORDERS</p>
          <p className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{(ws.live_orders_count || 0).toLocaleString('en-IN')}</p>
        </div>
        <div>
          <p className="text-[9px]" style={{ color: '#9CA3AF' }}>CSV HISTORICAL</p>
          <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{(ws.csv_orders_count || 0).toLocaleString('en-IN')}</p>
        </div>
      </div>
      <p className="text-[10px] mt-2" style={{ color: status === 'empty' ? '#6B7280' : '#374151' }}>
        {status === 'live' && 'Live stream active — Shopify Flow webhook is firing.'}
        {status === 'historical' && `CSV upload is the active source. Last batch: ${(ws.csv_orders_count || 0).toLocaleString('en-IN')} orders. Aditya refreshes with a fresh CSV export whenever needed.`}
        {status === 'empty' && 'No orders yet. Upload a CSV or configure live webhook.'}
      </p>
      {status === 'historical' && (
        <details className="mt-2">
          <summary className="text-[10px] cursor-pointer" style={{ color: '#9CA3AF' }}>
            Want live streaming instead? (optional, for later)
          </summary>
          <a href="/docs/shopify-flow-setup" target="_blank" rel="noreferrer" className="text-[10px] font-semibold inline-block mt-1" style={{ color: '#2A7B88' }}>
            → Shopify Flow 3-step setup guide
          </a>
        </details>
      )}
    </div>
  );
}

